
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SocialBlockConfiguration, SocialLink } from "@/types/block-configurations";
import {
  AlignmentControls,
  PaddingControls,
  ResponsiveControls,
} from "../shared";

interface SocialBlockPropertiesProps {
  config: SocialBlockConfiguration;
  onChange: (config: SocialBlockConfiguration) => void;
}

// tiktok is excluded — MJML has no built-in icon for it, so the email renderer skips it
const PLATFORMS: SocialLink['platform'][] = ['facebook', 'twitter', 'instagram', 'linkedin', 'youtube', 'pinterest', 'github'];

export function SocialBlockProperties({ config, onChange }: SocialBlockPropertiesProps) {
  const [newPlatform, setNewPlatform] = useState<SocialLink['platform']>('facebook');
  const [newUrl, setNewUrl] = useState('');

  const updateField = <K extends keyof SocialBlockConfiguration>(
    field: K,
    value: SocialBlockConfiguration[K]
  ) => {
    onChange({ ...config, [field]: value });
  };

  const links = config.links ?? [];

  const addLink = () => {
    if (!newUrl.trim()) return;
    const existing = links.find(l => l.platform === newPlatform);
    if (existing) {
      updateField('links', links.map(l => l.platform === newPlatform ? { ...l, url: newUrl.trim() } : l));
    } else {
      updateField('links', [...links, { platform: newPlatform, url: newUrl.trim() }]);
    }
    setNewUrl('');
  };

  const removeLink = (platform: string) => {
    updateField('links', links.filter(l => l.platform !== platform));
  };

  const updateLinkUrl = (platform: string, url: string) => {
    updateField('links', links.map(l => l.platform === platform ? { ...l, url } : l));
  };

  return (
    <div className="space-y-3">
      {/* Layout */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-medium mb-1 block">Layout</Label>
          <Select
            value={config.layout}
            onChange={(e) => updateField('layout', e.target.value as SocialBlockConfiguration['layout'])}
          >
            <option value="horizontal">Horizontal</option>
            <option value="vertical">Vertical</option>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-medium mb-1 block">Icon Shape</Label>
          <Select
            value={config.iconShape}
            onChange={(e) => updateField('iconShape', e.target.value as SocialBlockConfiguration['iconShape'])}
          >
            <option value="circle">Circle</option>
            <option value="square">Square</option>
            <option value="rounded">Rounded</option>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-medium mb-1 block">Icon Size (px)</Label>
          <Input
            type="number"
            value={config.iconSize}
            onChange={(e) => {
              const parsed = parseInt(e.target.value);
              updateField('iconSize', Number.isNaN(parsed) ? 32 : parsed);
            }}
            min={16}
            max={80}
          />
        </div>
        <div>
          <Label className="text-xs font-medium mb-1 block">Spacing (px)</Label>
          <Input
            type="number"
            value={config.iconSpacing}
            onChange={(e) => {
              const parsed = parseInt(e.target.value);
              updateField('iconSpacing', Number.isNaN(parsed) ? 10 : parsed);
            }}
            min={0}
            max={50}
          />
        </div>
      </div>

      <Accordion type="multiple" defaultValue={["links"]} className="w-full">

        <AccordionItem value="links">
          <AccordionTrigger>Social Links</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-2">
              {links.map((link) => (
                <div key={link.platform} className="flex gap-2 items-center">
                  <span className="shrink-0 w-20 text-xs font-medium capitalize">{link.platform}</span>
                  <Input
                    type="url"
                    value={link.url}
                    onChange={(e) => updateLinkUrl(link.platform, e.target.value)}
                    placeholder={`https://${link.platform}.com/yourpage`}
                    className="flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => removeLink(link.platform)}
                    className="shrink-0 rounded p-1.5 text-destructive hover:bg-destructive/10 transition-colors"
                    title="Remove"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
              <div className="flex gap-2 mt-2">
                <Select
                  value={newPlatform}
                  onChange={(e) => setNewPlatform(e.target.value as SocialLink['platform'])}
                  className="flex-1"
                >
                  {PLATFORMS.map(p => (
                    <option key={p} value={p} className="capitalize">{p}</option>
                  ))}
                </Select>
                <Input
                  type="url"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  placeholder="https://..."
                  className="flex-1"
                  onKeyDown={(e) => { if (e.key === 'Enter') addLink(); }}
                />
                <button
                  type="button"
                  onClick={addLink}
                  className="shrink-0 rounded border border-border bg-background px-2 py-1.5 text-xs hover:bg-accent transition-colors"
                >
                  Add
                </button>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="alignment">
          <AccordionTrigger>Alignment</AccordionTrigger>
          <AccordionContent>
            <AlignmentControls
              value={config.alignment}
              onChange={(alignment) => updateField('alignment', alignment)}
              showVertical={false}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="spacing">
          <AccordionTrigger>Spacing</AccordionTrigger>
          <AccordionContent>
            <PaddingControls
              value={config.padding}
              onChange={(padding) => updateField('padding', padding)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="responsive">
          <AccordionTrigger>Responsive</AccordionTrigger>
          <AccordionContent>
            <ResponsiveControls
              value={config.responsive}
              onChange={(responsive) => updateField('responsive', responsive)}
            />
          </AccordionContent>
        </AccordionItem>

      </Accordion>
    </div>
  );
}
